import { useState, useRef, useCallback } from 'react';
import {
  Heading,
  Text,
  Badge,
  CodeBlock,
  AgentPrompt,
  AssemblyStep,
  SimulatedAgentAdapter,
} from '@bq/web-components';
import type { AssemblyStepData } from '@bq/web-components';
import styles from './BuildWithMeSection.module.css';

const agent = new SimulatedAgentAdapter();

export function BuildWithMeSection() {
  const [steps, setSteps] = useState<AssemblyStepData[]>([]);
  const [running, setRunning] = useState(false);
  const [finalPreview, setFinalPreview] = useState<string | null>(null);
  const [sequenceTitle, setSequenceTitle] = useState<string | null>(null);
  const abortRef = useRef(false);

  const handleSubmit = useCallback(async (prompt: string) => {
    const sequence = agent.match(prompt);
    if (!sequence) return;

    abortRef.current = false;
    setSteps([]);
    setFinalPreview(null);
    setSequenceTitle(sequence.title);
    setRunning(true);

    for await (const step of agent.execute(sequence)) {
      if (abortRef.current) break;
      setSteps((prev) => [...prev, step]);
    }

    if (!abortRef.current) {
      setFinalPreview(sequence.finalPreview);
    }
    setRunning(false);
  }, []);

  return (
    <section
      id="build"
      data-crd-id="r4.bq-build-with-me-section"
      data-crd-layer="r4"
      className={styles.section}
    >
      <div className={styles.intro}>
        <Badge variant="default">Feature #2</Badge>
        <Heading level={2} as={2} align="center">
          Build With Me
        </Heading>
        <Text size="r1" align="center" color="n500">
          Type a prompt and watch the agent assemble a CRD component
          step-by-step — from R1 tokens up through R2 elements into R3
          compositions.
        </Text>
      </div>

      <div className={styles.workspace}>
        <AgentPrompt onSubmit={handleSubmit} disabled={running} />

        {sequenceTitle && (
          <Text size="r3" weight="bold" color="p500">
            {running ? 'Building' : 'Built'}: {sequenceTitle}
          </Text>
        )}

        {steps.length > 0 && (
          <div className={styles.steps}>
            {steps.map((step, i) => (
              <AssemblyStep
                key={i}
                layer={step.layer}
                action={step.action}
                code={step.code}
                index={i}
              />
            ))}
          </div>
        )}

        {running && (
          <div className={styles.spinner}>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
          </div>
        )}

        {finalPreview && (
          <div className={styles.result}>
            <Text size="r3" weight="bold" color="n500">
              Final Component
            </Text>
            <CodeBlock>{finalPreview}</CodeBlock>
          </div>
        )}
      </div>
    </section>
  );
}
